<script lang="ts">
	import ArticleFeaturedImage from '$lib/components/blog/article/ArticleFeaturedImage.svelte';
	export let post: any;
	export let type: string = post?.type;
	export let view: boolean = true; // true = display mode, false = edit/create

	const formatDateToYYYYMMDD = (date: Date | string | undefined): string | undefined => {
		if (!date) return undefined;
		if (typeof date === 'string') return date;
		return date.toISOString().split('T')[0];
	};

	const formatDateRange = (
		startDate: string | Date | undefined,
		endDate: string | Date | undefined
	) => {
		const startStr = formatDateToYYYYMMDD(startDate);
		const endStr = formatDateToYYYYMMDD(endDate);

		if (!startStr || !endStr) return startStr || '';

		const start = new Date(startStr + 'T12:00:00');
		const end = new Date(endStr + 'T12:00:00');

		// If same day, just return the formatted start date
		if (start.getTime() === end.getTime()) {
			return start.toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'long',
				day: 'numeric'
			});
		}

		// Format start date
		const startMonth = start.toLocaleDateString('en-US', { month: 'long' });
		const startDay = start.toLocaleDateString('en-US', { day: 'numeric' });
		const startYear = start.toLocaleDateString('en-US', { year: 'numeric' });

		// Format end date
		const endMonth = end.toLocaleDateString('en-US', { month: 'long' });
		const endDay = end.toLocaleDateString('en-US', { day: 'numeric' });
		const endYear = end.toLocaleDateString('en-US', { year: 'numeric' });

		// If same year
		if (startYear === endYear) {
			// If same month
			if (startMonth === endMonth) {
				return `${startMonth} ${startDay} to ${endDay}, ${startYear}`;
			}
			// Different months, same year
			return `${startMonth} ${startDay} to ${endMonth} ${endDay}, ${startYear}`;
		}
		// Different years (e.g., Dec 24 - Jan 5, 2025)
		return `${startMonth} ${startDay} to ${endMonth} ${endDay}, ${endYear}`;
	};
</script>

{#if type === 'event'}
	<div class="pb-8 font-exo-2 pt-3 md:-mb-3 lg:mb-0">
		<div class="grid md:grid-cols-2 -mb-10 2xl:-mb-6">
			<div class="md:col-span-1">
				<div class="grid grid-cols-1 gap-3">
					<div class="mt-2">
						<span class="text-cosmicPurple100 text-sm">
							{#if post.event_date_start && post.event_date_end && post.event_date_start !== post.event_date_end}
								Dates
							{:else}
								Date
							{/if}
						</span>
						<div class="text-gray-200">
							{formatDateRange(post.event_date_start, post.event_date_end)}
						</div>
					</div>
					<div class="grid gap-0">
						{#if post.event_time_start}
							<span class="text-cosmicPurple100 text-sm"> Time </span>
							<div class="flex mb-1">
								<div class="flex">
									<div class="w-full text-left text-gray-200">
										{post.event_time_start}
									</div>
									{#if post.event_time_end}
										<div class="w-full row-span-4 text-left text-gray-200">
											-{post.event_time_end}
										</div>
									{/if}
								</div>
							</div>
						{/if}
					</div>
					<div class="grid grid-rows-4 gap-0">
						<span class="text-cosmicPurple100 text-sm"> Location</span>
						<div class="w-full row-span-4 text-left mb-4 text-gray-200">
							{post.event_location}
						</div>
					</div>
				</div>
			</div>
			<div
				class="order-last md:col-span-1 md:content-end justify-center items-center sm:mt-8 md:mt-0 pb-5 md:pb-0 lg:pb-5 md:px-5 md:self-start"
			>
				<ArticleFeaturedImage image_url={post.image_url} view={true} />
			</div>
		</div>
	</div>
{:else if type === 'action'}
	<div class="text-gray-200 font-exo-2 pb-8 pt-3 md:-mb-3 lg:mb-0">
		<div
			class="md:grid md:grid-cols-2 md:gap-x-8 4xl:grid-rows-[auto_auto] 4xl:grid-cols-2 4xl:gap-x-12 items-start"
		>
			<div class="md:col-span-1 lg:-mr-5 4xl:row-span-1 mt-2">
				<div class="grid grid-cols-1 gap-1 md:my-2 lg:my-3 3xl:my-4">
					<div class="mb-2">
						<div class="text-cosmicPurple100 text-sm">
							{#if post.event_date_start && post.event_date_end && post.event_date_start !== post.event_date_end}
								dates
							{/if}
						</div>
						<div class="text-gray-200">
							{formatDateRange(post.event_date_start, post.event_date_end)}
						</div>
					</div>
					<div class="mb-2">
						<div class="text-cosmicPurple100 text-sm">participants</div>
						<div class="text-lg lg:text-xl xl:text-lg 3xl:text-xl">
							{post.participants}
						</div>
					</div>
					{#if post.objective && post.objective !== 'null' && post.objective.trim() !== ''}
						<div class="mb-2">
							<span class="text-cosmicPurple100 text-xs md:text-sm"> objective</span>
							<div class="w-full row-span-4 text-left text-gray-200">
								{post.objective}
							</div>
						</div>
					{/if}
					{#if post.impacts && post.impacts.length > 0}
						<div class="mt-8">
							<div class="text-left font-semibold md:text-md -mb-2 lg:text-xl text-white">
								<p>IMPACTS</p>
							</div>
							<div class="text-sm lg:text-base xl:text-xl text-gray-200 w-full mb-5">
								<ul>
									{#each post.impacts as _impact}
										<li class="flex w-full lg:pl-4 pt-2">
											<span class="grow w-full 3xl:text-lg">- {_impact.text}</span>
										</li>
									{/each}
								</ul>
							</div>
						</div>
					{/if}
				</div>
			</div>
			<div class="md:col-span-1 md:content-center mx-5 mb-10 md:mb-0 4xl:row-span-2">
				<ArticleFeaturedImage image_url={post.image_url} view={true} />
			</div>
		</div>
	</div>
{:else if type === 'template'}
	<div class="grid md:grid-cols-2 font-exo-2 mb-3 4xl:gap-x-12">
		<div class="order-1 md:order-first md:col-span-1">
			<div class="grid grid-cols-1 gap-2 md:gap-1 md:my-2 lg:my-3 2xl:my-4">
				<div class="grid grid-rows-3 gap-0">
					{#if post.community}
						<div class="mt-2 row-span-1 text-cosmicPurple100 text-sm">
							community this template came from
						</div>
						<div class="row-span-3 mt-1 w-full break-all text-gray-200 underline">
							<a
								class="text-bloomBlueLightActive"
								target="_blank"
								rel="noreferrer noopener"
								href={post.website}>{post.community}</a
							>
						</div>
					{/if}
				</div>
				<div class="grid grid-rows-2 gap-0">
					<div class="mt-2 text-cosmicPurple100 text-sm">summary</div>
					<div class="w-full text-left text-gray-200">
						{post.description}
					</div>
				</div>
			</div>
		</div>
		<div
			class="order-2 md:order-last md:self-start mt-4 md:mt-0 pb-2 md:pb-5 4xl:max-h-60 w-full md:max-w-[40vw] 4xl:max-w-[22vw] overflow-hidden rounded-2xl"
		>
			<ArticleFeaturedImage image_url={post.image_url} view={true} />
		</div>
	</div>
{:else}
	<!-- Blog/creative default -->
	<div
		class="bg-opacity-75 bg-cover text-white text-left pb-5 lg:pb-10 mx-auto 3xl:max-w-full overflow-x-hidden rounded-t-2xl font-exo-2"
	>
		<div class="grid grid-cols-1">
			<!-- <div class="md:col-span-1">
				<div class="grid grid-cols-1 gap-3 md:mt-10">
					<div class="grid grid-rows-4 gap-0">
						<div class="row-span-1 text-cosmicPurple100 text-sm">summary</div>
						<div class="row-span-3 text-gray-200">
							{post.description}
						</div>
					</div>
				</div>
			</div> -->
			<!-- <div
				class="order-first md:order-last md:col-span-1 md:content-end justify-center items-center md:mt-2 pb-5 md:px-5 ml-2"
			> -->
			<div class="flex justify-center items-center pb-5 md:px-5 w-full mt-5">
				<ArticleFeaturedImage image_url={post.image_url} view={true} />
			</div>
		</div>
	</div>
{/if}

<style>
	.post-meta {
		font-size: 1rem;
	}
</style>
